import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
export default function App() {
  const idleVideoRef = useRef(null);
  const talkVideoRef = useRef(null);
  const fileInputRef = useRef(null);
  const micButtonRef = useRef(null);
  const userInputRef = useRef(null);
  const connectButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const destroyButtonRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [geminiResponse, setGeminiResponse] = useState("");
  const [preview, setPreview] = useState(null);
  const agentRef = useRef(null);
  const [chat, setChat] = useState(() => {
    const saved = sessionStorage.getItem("chatHistory");
    return saved ? JSON.parse(saved) : [];
  });




  class VideoAgent {
    constructor({ refs }) {
      this.peerConnection = null;
      this.streamId = null;
      this.sessionId = null;
      this.statsIntervalId = null;
      this.API_CONFIG = null;
      this.DID_API_URL = process.env.REACT_APP_DID_URL;
      this.lastBytesReceived = 0;
      this.videoIsPlaying = false;
      this.refs = refs;
    }

    async init() {
      try {
        if (!process.env.REACT_APP_DID_KEY) {
          console.error("Missing D-ID API key");
        }

        this.API_CONFIG = { key: process.env.REACT_APP_DID_KEY };
        this.DID_API_URL = process.env.REACT_APP_DID_URL || "https://api.d-id.com";
        this.VOICE_ID = process.env.REACT_APP_VOICE_ID;

        this.setupEventListeners();
      } catch (error) {
        this.showError(`Initialization failed: ${error.message}`);
      }
    }


    setupEventListeners() {
      const connectButton = this.refs.connectButton.current;
      const uploadButton = this.refs.uploadButton.current;
      const destroyButton = this.refs.destroyButton.current;
      const enterButton = document.getElementById("enter-button");
      const micButton = this.refs.micButton?.current;
      const themeToggle = document.getElementById("theme-toggle");
    }

    async handleConnect() {
      const connectButton = this.refs.connectButton.current;
      if (connectButton) connectButton.classList.add('loading');

      try {
        if (!window.avatarUrl) {
          if (connectButton) { connectButton.classList.remove('loading'); connectButton.innerText = ""; }
          toast.error("Please upload an avatar first!");
          connectButton.innerText = "Connect";
          return;
        }
        if (this.peerConnection?.connectionState === "connected") return;
        this.cleanup();
        const response = await this.createStream();
        const body = await response.json();
        const { id, offer, ice_servers, session_id } = body;
        this.streamId = id;
        this.sessionId = session_id;

        const answer = await this.createPeerConnection(offer, ice_servers);
        await this.sendSDPAnswer(answer);
        if (connectButton) {
          connectButton.classList.remove("loading");
          connectButton.classList.add("connected");
          connectButton.innerText = "Connected";
        }
        toast.success('Connection Successful')
        if (userInputRef.current) userInputRef.current.focus();
      } catch (error) {
        this.showError(`Connection failed: Try uploading a different image`);
        if (connectButton) connectButton.classList.remove("loading", "connected");
        this.cleanup();
      }
    }

    async handleTalk() {
      try {
        const userMessage = userInputRef.current?.value?.trim();
        if (!userMessage) {
          toast.error("Please enter a query");
          return;
        }

        const idleVideo = idleVideoRef.current;
        const originalSrc = "/default-graphic.mp4"; // initial idle video
        let keywordVideo = null;

        // ✅ map keywords to videos
        if (userMessage.toLowerCase().includes('infra')) {
          keywordVideo = "/Recording 2025-09-13 113341.mp4";
        }

        if (keywordVideo && idleVideo) {
          // Stop looping while keyword video plays
          idleVideo.removeAttribute("loop");
          idleVideo.src = keywordVideo;
          idleVideo.play().catch(console.error);

          idleVideo.onended = () => {
            idleVideo.src = originalSrc;
            idleVideo.setAttribute("loop", ""); // restore looping
            idleVideo.play().catch(console.error);
            idleVideo.onended = null;
          };

          return;
        }


        // Normal flow for other messages
        setChat(prev => {
          const updated = [...prev, { sender: "user", text: userMessage }];
          sessionStorage.setItem("chatHistory", JSON.stringify(updated));
          return updated;
        });

        if (inputContainerRef.current) inputContainerRef.current.classList.add('loading');

        const aiResponse = await this.fetchGeminiResponse(userMessage);

        setGeminiResponse(aiResponse);
        setChat(prev => {
          const updated = [...prev, { sender: "assistant", text: aiResponse }];
          sessionStorage.setItem("chatHistory", JSON.stringify(updated));
          return updated;
        });

        await this.sendTalkRequest(aiResponse);

        if (inputContainerRef.current) inputContainerRef.current.classList.remove('loading');
        if (userInputRef.current) userInputRef.current.value = '';
      } catch (error) {
        console.error('Talk Error:', error);
        window.alert(`Error: ${error.message}`);
        if (inputContainerRef.current) inputContainerRef.current.classList.remove('loading');
      }
    }


    async fetchGeminiResponse(prompt) {
      const res = await fetch(process.env.REACT_APP_SERVER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      if (!res.ok) {
        const text = await res.text();
        console.error(`Gemini server error: ${text}`);
      }
      const data = await res.json();
      return data.response;
    }

    async handleDestroy() {
      try {
        if (this.streamId) await this.deleteStream();
      } catch (error) {
        console.error('Destroy error:', error);
      } finally {
        this.cleanup();

        const connectButton = this.refs.connectButton.current;
        const uploadButton = this.refs.uploadButton.current;

        if (connectButton) {
          connectButton.classList.remove('connected', 'loading');
          connectButton.innerText = "Connect";
        }
        if (uploadButton) {
          uploadButton.disabled = false;
          uploadButton.classList.remove("uploaded", "loading");
          uploadButton.innerText = "Upload Avatar";
        }
        sessionStorage.removeItem("chatHistory");
        setChat([]);
        setGeminiResponse("");
        setPreview(null);
        window.avatarUrl = null;
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.info("Session disconnected");
      }
    }




    async createStream() {
      return fetch(`${this.DID_API_URL}/talks/streams`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(this.API_CONFIG.key + ':')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_url: window.avatarUrl,
          stream_warmup: true,
          config: { video_quality: 'hd' }
        })
      });
    }

    async createPeerConnection(offer, iceServers) {
      const RTCPeerConnection = (
        window.RTCPeerConnection ||
        window.webkitRTCPeerConnection ||
        window.mozRTCPeerConnection
      ).bind(window);

      this.peerConnection = new RTCPeerConnection({ iceServers });

      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          fetch(`${this.DID_API_URL}/talks/streams/${this.streamId}/ice`, {
            method: 'POST',
            headers: {
              'Authorization': `Basic ${btoa(this.API_CONFIG.key + ':')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
              session_id: this.sessionId
            })
          }).catch(console.error);
        }
      };

      this.peerConnection.ontrack = (event) => {
        if (event.track.kind === 'video') {
          this.statsIntervalId = setInterval(async () => {
            const stats = await this.peerConnection.getStats(event.track);
            stats.forEach((report) => {
              if (report.type === 'inbound-rtp' && report.kind === 'video') {
                const isPlaying = report.bytesReceived > this.lastBytesReceived;
                if (isPlaying !== this.videoIsPlaying) {
                  this.videoIsPlaying = isPlaying;

                  if (isPlaying) {
                    if (this.refs.idleVideo.current) this.refs.idleVideo.current.style.display = 'none';
                    if (this.refs.talkVideo.current) {
                      this.refs.talkVideo.current.style.display = 'block';
                      this.refs.talkVideo.current.srcObject = event.streams[0];
                      this.refs.talkVideo.current.play().catch(console.error);
                    }

                  } else {
                    if (this.refs.talkVideo.current) {
                      this.refs.talkVideo.current.pause();
                      this.refs.talkVideo.current.srcObject = null;
                      this.refs.talkVideo.current.style.display = 'none';
                    }
                    if (this.refs.idleVideo.current) this.refs.idleVideo.current.style.display = 'block';
                  }
                }
                this.lastBytesReceived = report.bytesReceived;
              }
            });
          }, 500);
        }
      };
      await this.peerConnection.setRemoteDescription(offer);
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      return answer;
    }

    async sendSDPAnswer(answer) {
      return fetch(`${this.DID_API_URL}/talks/streams/${this.streamId}/sdp`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(this.API_CONFIG.key + ':')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answer, session_id: this.sessionId })
      });
    }

    async sendTalkRequest(aiResponse) {
      return fetch(`${this.DID_API_URL}/talks/streams/${this.streamId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(this.API_CONFIG.key + ':')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          script: {
            type: 'text',
            input: aiResponse,
            provider: {
              type: 'microsoft',
              voice_id: this.VOICE_ID,
              voice_config: { style: 'Cheerful' }
            }
          },
          config: { fluent: true, stitch: true },
          driver_url: 'bank://lively/',
          session_id: this.sessionId
        })
      });
    }

    async deleteStream() {
      return fetch(`${this.DID_API_URL}/talks/streams/${this.streamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${btoa(this.API_CONFIG.key + ':')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_id: this.sessionId })
      });
    }

    cleanup() {
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }
      if (this.refs.talkVideo.current) {
        this.refs.talkVideo.current.pause();
        this.refs.talkVideo.current.srcObject = null;
        this.refs.talkVideo.current.style.display = 'none';
      }
      if (this.refs.idleVideo.current) this.refs.idleVideo.current.style.display = 'block';

      if (this.statsIntervalId) {
        clearInterval(this.statsIntervalId);
        this.statsIntervalId = null;
      }
    }
    showError(message) {
      toast.info(message);
      console.error(message);
    }
    async handleVoiceInput() {
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) console.error('Speech recognition not supported.');

        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
          toast.error("No microphone detected or permission denied.");
        }
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        if (this.refs.micButton?.current) {
          this.refs.micButton.current.classList.add('listening');
        }

        recognition.start();

        recognition.onresult = async (event) => {
          const transcript = event.results[0][0].transcript;
          if (userInputRef.current) userInputRef.current.value = transcript;
          await this.handleTalk();
        };

        recognition.onend = () => {
          if (this.refs.micButton?.current) {
            this.refs.micButton.current.classList.remove('listening');
          }
        };
      } catch (error) {
        if (this.refs.micButton?.current) {
          this.refs.micButton.current.classList.remove('listening');
        }
        this.showError(`Voice input failed: ${error.message}`);
      }
    }

  }

  async function compressImage(file, maxWidth = 800, maxHeight = 800, quality = 0.7) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        let { width, height } = img;

        // maintain aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const aspect = width / height;
          if (width > height) {
            width = maxWidth;
            height = Math.round(maxWidth / aspect);
          } else {
            height = maxHeight;
            width = Math.round(maxHeight * aspect);
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Compression failed"));
              return;
            }
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function uploadImage() {
    const uploadButton = uploadButtonRef.current;
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Please select an avatar first!");
      return;
    }

    if (uploadButton) {
      uploadButton.disabled = true;
      uploadButton.innerText = "Uploading...";
    }

    try {
      // ✅ compress before upload
      const compressedFile = await compressImage(file, 800, 800, 0.7);

      const formData = new FormData();
      formData.append("file", compressedFile);
      formData.append("upload_preset", "vAssistant");
      formData.append("cloud_name", "djx2edbwi");

      const res = await fetch(process.env.REACT_APP_CLOUDINARY_URL, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.secure_url) {
        toast.success("Image Uploaded Successfully");

        if (uploadButton) {
          uploadButton.innerText = "Uploaded";
          uploadButton.classList.add("uploaded");
        }

        window.avatarUrl = data.secure_url;
      } else {
        toast.error("Upload failed, try another image");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Image upload failed. Please try again.");
    }
  }


  useEffect(() => {

    agentRef.current = new VideoAgent({
      refs: {
        idleVideo: idleVideoRef,
        talkVideo: talkVideoRef,
        connectButton: connectButtonRef,
        uploadButton: uploadButtonRef,
        destroyButton: destroyButtonRef,
        micButton: micButtonRef,
      }
    });
    agentRef.current.init();
    return () => {
      try { agentRef.current?.handleDestroy(); } catch (e) { /* ignore */ }
    };
  }, []);

  return (
    <div className="app-layout">
      <div id="content" className="p-2 max-w-3xl mx-auto">

        <img className="logo" src="o7ServicesLogo.png" alt="logo" width="150px" />

        <div id="video-wrapper" className="mb-2">
          <div id="video-container" className="relative">
            <video
              id="idle-video"
              ref={idleVideoRef}
              src="/default-graphic.mp4"
              autoPlay
              muted
              loop
              playsInline
              style={{ width: '100%', display: 'block' }}
            />
            <video
              id="talk-video"
              ref={talkVideoRef}
              autoPlay
              playsInline
              style={{ width: '100%', display: 'none' }}
            />
          </div>
        </div>

        <div id="buttons" className="flex gap-2 mb-2">
          <label htmlFor="fileInput" className="file-label">
            Choose Avatar
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg, image/jpg"
            id="fileInput"
            className="input-field"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const url = URL.createObjectURL(e.target.files[0]);
                setPreview(url);
              }
            }}
          />
          {preview && (
            <div className="preview-container">
              <img src={preview} alt="Preview" className="preview-image" />
            </div>
          )}
          <button
            ref={uploadButtonRef}
            onClick={uploadImage}
            className="px-3 py-2 rounded bg-slate-700 text-white"
          >
            Upload Avatar
          </button>
          <button
            ref={connectButtonRef}
            id="connect-button"
            type="button"
            onClick={() => agentRef.current?.handleConnect()}
            className="px-3 py-2 rounded bg-green-600 text-white button"
          >
            Connect
          </button>
          <button
            ref={destroyButtonRef}
            id="disconnect-button"
            type="button"
            onClick={() => agentRef.current?.handleDestroy()}
            className="px-3 py-2 rounded bg-red-600 text-white button"
          >
            Disconnect
          </button>
        </div>


        <hr id="divider-line" className="my-4" />

        <div id="input-container" ref={inputContainerRef} className="flex gap-2 items-center">
          <input
            ref={userInputRef}
            type="text"
            id="user-input-field"
            placeholder="Ask me anything..."
            className="flex-1 p-2 border rounded"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                agentRef.current?.handleTalk();
              }
            }}
          />
          <button
            id="enter-button"
            type="button"
            title="Send message"
            className="px-3 py-2 border rounded button"
            onClick={() => agentRef.current?.handleTalk()}

          >
            <i className="bi bi-arrow-right"></i>
          </button>
          <button
            id="mic-button"
            ref={micButtonRef}
            className="px-3 py-2 border rounded button"
            onClick={() => agentRef.current?.handleVoiceInput()}
          >
            <i className="bi bi-mic"></i>
          </button>


        </div>

        <audio id="audio-element" className="hidden-audio" autoPlay />
        <ToastContainer position="top-right" />
      </div>

      {/* Chat history panel */}
      <div className="chat-history-panel">
        <h1 className="title">CHAT HISTORY</h1>
        <div className="chat-window">
          {chat.map((msg, idx) => (
            <div
              key={idx}
              className={`chat-bubble ${msg.sender === "user" ? "user" : "assistant"}`}
            >
              {msg.text}
            </div>
          ))}
        </div>
      </div>
    </div>

  );
}